import { i18n } from '@/i18n'
import getNestedValue from '@/services/functions/getNestedValue'
import hasNestedValue from '@/services/functions/hasNestedValue'

const stringComparators = {
  contains: (a, b) => a.includes(b),
  not_contains: (a, b) => !a.includes(b),
  starts_with: (a, b) => a.startsWith(b),
  not_starts_with: (a, b) => !a.startsWith(b),
  ends_with: (a, b) => a.endsWith(b),
  not_ends_with: (a, b) => !a.endsWith(b),
  equals: (a, b) => a === b,
  not_equals: (a, b) => a !== b,
  equalsi: (a, b) => a.toLowerCase() === b.toLowerCase(),
  not_equalsi: (a, b) => a.toLowerCase() !== b.toLowerCase(),
}

/**
 * 
 * @param {*} value 
 * @returns {Boolean}
 */
function isPrimitive (value) {
  if (value === null || value === undefined) {
    return false
  }
  return !(typeof value === 'object' || typeof value === 'function')
}

/**
 * 
 * @param {String|Number|Boolean|BigInt|Date} value 
 * @param {'number'|'date'} type 
 * @returns {Number}
 */
function parseToNumber (value, type = 'number') {
  const allowedTypes = ['string', 'number', 'boolean', 'bigint']
  const valueType = typeof value
  var result = NaN
  if (allowedTypes.includes(valueType)) {
    if (type === 'number') {
      result = Number(value)
    }
  
    if (type === 'date') {
      const date = new Date(value)
      result = date.getTime()
    }
  }
  return Number.isNaN(result) ? NaN : result
}

/**
 * 
 * @param {*} value 
 * @returns {Boolean}
 */
function parseToBoolean (value) {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'false') return false
  }

  return !!value
}

/**
 * 
 * @param {*} value 
 * @returns {String}
 */
function parseToString (value) {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'string') {
    return value
  }
  if (isPrimitive(value)) {
    value = Object(value)
  }
  if (typeof value.toString === 'function') {
    return value.toString()
  }
  return JSON.stringify(value)
}

/**
 * 
 * @param {String} key 
 * @param {Object} item 
 * @param {String|Number} value 
 * @param {Object} extraparams 
 * @returns {Boolean}
 */
function filterBy (key, item, value, extraparams = {}) {
  if (!key || !item || value === null || value === undefined) {
    return false
  }
  const type = extraparams.compareType
  if (type === 'function') {
    if (typeof extraparams.callback !== 'function') {
      return true
    }
    return extraparams.callback(item, value, key)
  }

  var suffix = ''
  if (!hasNestedValue(key, item)) {
    const regex = /^(.*)(MIN|MAX|DESDE|HASTA)$/i
    const match = key.match(regex)
    if (match) {
      suffix = match ? match[2].toLowerCase() : null
      key = key.replace(regex, '$1')
      if (!hasNestedValue(key, item)) {
        return false
      } 
    }
  }
  const itemValue = getNestedValue(key, item)
  if (type) {
    if (type === 'number' || type === 'date') {
      const numberValue = parseToNumber(value, type)
      const numberItemValue = parseToNumber(itemValue, type)
      if (suffix === 'min' || suffix === 'desde') {
        return numberItemValue >= numberValue
      }
      if (suffix === 'max' || suffix === 'hasta') {
        return numberItemValue <= numberValue
      }
    }
    if (type === 'boolean') {
      const boolValue = parseToBoolean(value)
      const boolItemValue = parseToBoolean(itemValue)
      return boolItemValue === boolValue
    }
  }
  if (typeof itemValue === 'object' && typeof value === 'object') {
    if (Object.hasOwnProperty.call(itemValue, 'equals')) {
      return itemValue.equals(value)
    }
    if (Object.hasOwnProperty.call(value, 'equals')) {
      return value.equals(itemValue)
    }
  }
  const strValue = parseToString(value)
  const strItemValue = parseToString(itemValue)
  const comparator = stringComparators[extraparams.comparator || 'contains']
  return comparator(strItemValue, strValue)
}

async function getDataAsTable (data = [], order = {}, params = null) {
  try {
    const metaTags = ['page', 'paginate', 'results', 'extraparams']
    const extraparams = params.extraparams || {}
    const filters = Object.entries(params || {})
      .filter(([key, value]) => !metaTags.includes(key) && value !== null && value !== undefined)
      .map(([key, value]) => x => filterBy(key, x, value, extraparams[key]))
    const orders = Object.entries(order?.order ?? {})
      .map(([key, order]) => (a, b) => (-Number(getNestedValue(key, a) < getNestedValue(key, b)) || Number(getNestedValue(key, a) > getNestedValue(key, b)) || 0) * (order ? 1 : -1))
    if (filters.length) {
      data = data.filter(x => filters.reduce((acc, filter) => acc && filter(x), true))
    }
    if (orders.length) {
      data = data.sort((a, b) => orders.reduce((acc, x) => acc !== 0 ? acc : x(a, b), 0))
    }
    const meta = {
      total: data.length,
      currentPage: 1,
      lastPage: 1,
      perPage: data.length
    }
    if (params.paginate) {
      meta.currentPage = params.page || meta.currentPage
      meta.perPage = params.results || meta.perPage
      meta.lastPage = Math.ceil(meta.total / meta.perPage) || meta.lastPage
      data = data.slice(meta.perPage * (meta.currentPage - 1), meta.perPage * meta.currentPage)
    }
    return { blnError: false, msgError: null, codError: null, respuesta: data, meta }
  } catch (error) {
    return { blnError: true, msgError: i18n.t('feedback.error.internal_error_message'), codError: 'FRGDAT000', respuesta: [] }      
  }
}

export { getDataAsTable }
